import {CheckOutlined} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Row,
  Select,
  Switch
} from 'antd';
import type {Rule} from 'antd/es/form';
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';

import {ReactComponent as Delete} from '../../../assets/icons/deleteIcon.svg';
import {CommonErrors} from '../../../constants/Common';
import type {
  FacilityRow,
  LciFormValues,
  ProductionDataValues,
  ProductionSiteRow
} from '../../../services/interface/pcfTypes';
import usePCFStore from '../../../store/pcfStore/pcfStore';
import {LCI_FIELD_HINTS} from '../constants/lciFieldHints';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  ENERGY_TYPES,
  ENERGY_UNITS,
  HAS_PROCESS_EMISSIONS_OPTIONS,
  INDIAN_STATES,
  PROCESS_ALLOCATION_SCOPE_OPTIONS,
  PROCESS_EMISSION_TYPES,
  WASTE_CHECKLIST_OPTIONS,
  WASTE_SCOPE_OPTIONS,
  WASTE_TREATMENTS,
  WATER_STRESS_REGIONS
} from '../constants/pcfOptions';
import {useExpertMode} from '../hooks/useExpertMode';
import {A3ManufacturingKeys, A3ManufacturingLabels} from '../PCFKeyLabels';

import {
  resetA3FacilityAtIndex,
  syncA3FacilitiesFromProduction
} from './a3FacilitySync';
import {FormItemLabel} from './FormItemLabel';
import {getProductionFromForm} from './lciProductionUtils';
import {noWasteJustificationRule, positiveUnitsRule} from './lciValidation';
import AddButton from './PCFAddButton';
import {productionDataDefaults} from './ProductionDataSection';
import RemovableCard from './RemovableCard';

const BASE = 'a3';

type A3CategoryTabId = 'energy' | 'process' | 'water' | 'waste';

const A3_CATEGORY_TABS: Array<{id: A3CategoryTabId; label: string}> = [
  {id: 'energy', label: 'Energy'},
  {id: 'process', label: 'Process'},
  {id: 'water', label: 'Water'},
  {id: 'waste', label: 'Waste'}
];

const nonNegativeRule = (label: string): Rule => ({
  validator: async (_: unknown, value: unknown) => {
    if (value == null || value === '' || parseFloat(String(value)) < 0) {
      return await Promise.reject(new Error(CommonErrors.mandatory(label)));
    }

    await Promise.resolve();
  }
});

const rulesWhenConfigured = (rules: Rule[], validateFields: boolean): Rule[] =>
  validateFields ? rules : [];

const blankEnergy = (zone = 'tamil_nadu') => ({
  type: 'grid_electricity',
  quantity: '',
  unit: 'kwh',
  zone,
  ef: '',
  rec: false,
  recQty: ''
});

const blankProcess = () => ({
  type: 'co2_fossil_process',
  qty: '',
  gwp: '1'
});

const blankWater = () => ({
  consumption: '',
  region: 'indus_basin'
});

const blankWaste = (type = 'solid_non_hazardous') => ({
  type,
  qty: '',
  treatment: 'landfilled',
  wasteScope: 'whole_facility_units',
  ef: ''
});

export const a3ManufacturingDefaults = {
  facilities: [] as FacilityRow[],
  activeFacility: null as number | null
};

const facilityPath = (
  facilityName: number,
  ...rest: Array<string | number>
) => [BASE, A3ManufacturingKeys.FACILITIES, facilityName, ...rest];

const getStateLabel = (stateValue: string): string =>
  INDIAN_STATES.find((option) => option.value === stateValue)?.label ??
  stateValue;

const getFacilityDisplayName = (
  facility: FacilityRow,
  productionSite: ProductionSiteRow | undefined,
  index: number
): string => {
  const fromProduction = productionSite?.name?.trim();
  const fromFacility = facility.name?.trim();

  if (fromProduction) {
    return fromProduction;
  }

  if (fromFacility) {
    return fromFacility;
  }

  return `Site ${index + 1}`;
};

const isMultiSiteSetup = (setup: string | undefined): boolean =>
  setup === 'multiple_sites' || setup === 'contract_manufacturer';

const resolveActiveFacilityIndex = (
  facilities: FacilityRow[],
  activeFacility: number | null | undefined
): number | null => {
  if (!facilities.length) {
    return null;
  }

  if (
    activeFacility != null &&
    activeFacility >= 0 &&
    activeFacility < facilities.length
  ) {
    return activeFacility;
  }

  return 0;
};

function EnergyFields({
  facilityName,
  energyName,
  expertMode,
  defaultZone,
  validateFields
}: {
  facilityName: number;
  energyName: number;
  expertMode: boolean;
  defaultZone: string;
  validateFields: boolean;
}) {
  const form = Form.useFormInstance();
  const listBasePath = facilityPath(facilityName, A3ManufacturingKeys.ENERGY);
  const energyType = Form.useWatch(
    [...listBasePath, energyName, A3ManufacturingKeys.ENERGY_TYPE],
    form
  );

  useEffect(() => {
    if (!defaultZone) {
      return;
    }

    const zonePath = facilityPath(
      facilityName,
      A3ManufacturingKeys.ENERGY,
      energyName,
      A3ManufacturingKeys.GRID_ZONE
    );
    const currentZone = form.getFieldValue(zonePath);

    if (currentZone == null || currentZone === '') {
      form.setFieldValue(zonePath, defaultZone);
    }
  }, [defaultZone, energyName, facilityName, form]);

  return (
    <Row gutter={rowGutter}>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[energyName, A3ManufacturingKeys.ENERGY_TYPE]}
          label={A3ManufacturingLabels.ENERGY_TYPE}
          rules={rulesWhenConfigured(
            [
              {
                required: true,
                message: CommonErrors.mandatory(A3ManufacturingLabels.ENERGY_TYPE)
              }
            ],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <Select className="select" options={ENERGY_TYPES} />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[energyName, A3ManufacturingKeys.TOTAL_QUANTITY]}
          required={validateFields}
          label={
            <FormItemLabel hint={LCI_FIELD_HINTS.quantity}>
              {A3ManufacturingLabels.TOTAL_QUANTITY}
            </FormItemLabel>
          }
          rules={rulesWhenConfigured(
            [positiveUnitsRule(A3ManufacturingLabels.TOTAL_QUANTITY)],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <InputNumber
            controls={false}
            className="input"
            placeholder="Total consumed"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[energyName, A3ManufacturingKeys.ENERGY_UNIT]}
          label={A3ManufacturingLabels.ENERGY_UNIT}
          rules={rulesWhenConfigured(
            [
              {
                required: true,
                message: CommonErrors.mandatory(A3ManufacturingLabels.ENERGY_UNIT)
              }
            ],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <Select className="select" options={ENERGY_UNITS} />
        </Form.Item>
      </Col>
      {energyType === 'grid_electricity' && (
        <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
          <Form.Item
            name={[energyName, A3ManufacturingKeys.GRID_ZONE]}
            label={A3ManufacturingLabels.GRID_ZONE}
            rules={rulesWhenConfigured(
              [
                {
                  required: true,
                  message: CommonErrors.mandatory(A3ManufacturingLabels.GRID_ZONE)
                }
              ],
              validateFields
            )}
            validateFirst
            hasFeedback
          >
            <Select
              className="select"
              options={INDIAN_STATES}
              disabled={!expertMode}
            />
          </Form.Item>
        </Col>
      )}
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[energyName, A3ManufacturingKeys.ENERGY_EMISSION_FACTOR]}
          required={validateFields && expertMode}
          label={A3ManufacturingLabels.ENERGY_EMISSION_FACTOR}
          rules={rulesWhenConfigured(
            expertMode
              ? [
                  positiveUnitsRule(
                    A3ManufacturingLabels.ENERGY_EMISSION_FACTOR
                  )
                ]
              : [],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          {expertMode ? (
            <InputNumber
              controls={false}
              className="input"
              style={{width: '100%'}}
              min={0}
            />
          ) : (
            <Input
              className="input"
              disabled
              placeholder="Auto from database"
            />
          )}
        </Form.Item>
      </Col>
      {expertMode && (
        <>
          <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
            <Form.Item
              name={[energyName, A3ManufacturingKeys.REC_ENABLED]}
              label={A3ManufacturingLabels.REC_ENABLED}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
            <Form.Item
              name={[energyName, A3ManufacturingKeys.REC_QUANTITY]}
              label={A3ManufacturingLabels.REC_QUANTITY}
            >
              <InputNumber
                controls={false}
                className="input"
                placeholder="e.g. 500,000"
                style={{width: '100%'}}
                min={0}
              />
            </Form.Item>
          </Col>
        </>
      )}
    </Row>
  );
}

function ProcessFields({
  processName,
  validateFields
}: {
  processName: number;
  validateFields: boolean;
}) {
  return (
    <Row gutter={rowGutter}>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[processName, A3ManufacturingKeys.PROCESS_EMISSION_TYPE]}
          label={A3ManufacturingLabels.PROCESS_EMISSION_TYPE}
          rules={rulesWhenConfigured(
            [
              {
                required: true,
                message: CommonErrors.mandatory(
                  A3ManufacturingLabels.PROCESS_EMISSION_TYPE
                )
              }
            ],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <Select className="select" options={PROCESS_EMISSION_TYPES} />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[processName, A3ManufacturingKeys.PROCESS_EMISSION_QUANTITY]}
          label={A3ManufacturingLabels.PROCESS_EMISSION_QUANTITY}
          rules={rulesWhenConfigured(
            [nonNegativeRule(A3ManufacturingLabels.PROCESS_EMISSION_QUANTITY)],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[processName, A3ManufacturingKeys.GWP100_FACTOR]}
          label={A3ManufacturingLabels.GWP100_FACTOR}
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
          />
        </Form.Item>
      </Col>
    </Row>
  );
}

function WaterFields({waterName}: {waterName: number}) {
  return (
    <Row gutter={rowGutter}>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[waterName, A3ManufacturingKeys.WATER_CONSUMPTION]}
          label={A3ManufacturingLabels.WATER_CONSUMPTION}
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[waterName, A3ManufacturingKeys.WATER_STRESS_REGION]}
          label={A3ManufacturingLabels.WATER_STRESS_REGION}
        >
          <Select className="select" options={WATER_STRESS_REGIONS} />
        </Form.Item>
      </Col>
    </Row>
  );
}

function WasteFields({
  wasteName,
  validateFields
}: {
  wasteName: number;
  validateFields: boolean;
}) {
  return (
    <Row gutter={rowGutter}>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[wasteName, A3ManufacturingKeys.WASTE_TYPE]}
          label={A3ManufacturingLabels.WASTE_TYPE}
          rules={rulesWhenConfigured(
            [
              {
                required: true,
                message: CommonErrors.mandatory(A3ManufacturingLabels.WASTE_TYPE)
              }
            ],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <Select className="select" options={WASTE_CHECKLIST_OPTIONS} />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[wasteName, A3ManufacturingKeys.WASTE_QUANTITY]}
          label={A3ManufacturingLabels.WASTE_QUANTITY}
          rules={rulesWhenConfigured(
            [nonNegativeRule(A3ManufacturingLabels.WASTE_QUANTITY)],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[wasteName, A3ManufacturingKeys.WASTE_TREATMENT]}
          label={A3ManufacturingLabels.WASTE_TREATMENT}
          rules={rulesWhenConfigured(
            [
              {
                required: true,
                message: CommonErrors.mandatory(
                  A3ManufacturingLabels.WASTE_TREATMENT
                )
              }
            ],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <Select className="select" options={WASTE_TREATMENTS} />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[wasteName, A3ManufacturingKeys.WASTE_TREATMENT_EMISSION_FACTOR]}
          label={A3ManufacturingLabels.WASTE_TREATMENT_EMISSION_FACTOR}
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={24}>
        <Form.Item
          name={[wasteName, A3ManufacturingKeys.WASTE_SCOPE]}
          label={A3ManufacturingLabels.WASTE_SCOPE}
          rules={rulesWhenConfigured(
            [
              {
                required: true,
                message: CommonErrors.mandatory(A3ManufacturingLabels.WASTE_SCOPE)
              }
            ],
            validateFields
          )}
          validateFirst
          hasFeedback
        >
          <Radio.Group options={WASTE_SCOPE_OPTIONS} />
        </Form.Item>
      </Col>
    </Row>
  );
}

function A3CategoryTabs({
  active,
  onChange
}: {
  active: A3CategoryTabId;
  onChange: (id: A3CategoryTabId) => void;
}) {
  return (
    <div className="a3-category-tabs" role="tablist">
      {A3_CATEGORY_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={`a3-category-tab ${active === tab.id ? 'active' : ''}`}
          onClick={() => {
            onChange(tab.id);
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function EnergyTabPanel({
  facilityName,
  expertMode,
  siteState,
  validateFields,
  onConfigure
}: {
  facilityName: number;
  expertMode: boolean;
  siteState: string;
  validateFields: boolean;
  onConfigure: () => void;
}) {
  return (
    <div className="subsection-card">
      <Form.List
        name={[...facilityPath(facilityName), A3ManufacturingKeys.ENERGY]}
      >
        {(fields, {add, remove}) => (
          <>
            <div className="lci-section-header">
              <h4 className="subsection-title">Energy Inputs</h4>
              <AddButton
                onClick={() => {
                  onConfigure();
                  add(blankEnergy(siteState));
                }}
              >
                Add Energy Source
              </AddButton>
            </div>
            <div className="lci-cards-list">
              {fields.map(({key, name: energyName}, idx) => (
                <RemovableCard
                  key={key}
                  title={`#${idx + 1}`}
                  onRemove={() => {
                    remove(energyName);
                  }}
                  showRemove={fields.length > 1}
                >
                  <EnergyFields
                    facilityName={facilityName}
                    energyName={energyName}
                    expertMode={expertMode}
                    defaultZone={siteState}
                    validateFields={validateFields}
                  />
                </RemovableCard>
              ))}
            </div>
          </>
        )}
      </Form.List>
    </div>
  );
}

function ProcessTabPanel({
  facilityName,
  validateFields,
  onConfigure
}: {
  facilityName: number;
  validateFields: boolean;
  onConfigure: () => void;
}) {
  const form = Form.useFormInstance();
  const hasProcessEmissions = Form.useWatch(
    [...facilityPath(facilityName), A3ManufacturingKeys.HAS_PROCESS_EMISSIONS],
    form
  );

  return (
    <div className="subsection-card">
      <div className="lci-section-header">
        <h4 className="subsection-title">Process Emissions</h4>
      </div>
      <Form.Item
        name={[
          ...facilityPath(facilityName),
          A3ManufacturingKeys.HAS_PROCESS_EMISSIONS
        ]}
        label={A3ManufacturingLabels.HAS_PROCESS_EMISSIONS}
        rules={
          validateFields ? [{required: true, message: 'Required'}] : []
        }
      >
        <Radio.Group
          options={HAS_PROCESS_EMISSIONS_OPTIONS}
          onChange={onConfigure}
        />
      </Form.Item>
      {hasProcessEmissions === 'yes' && (
        <>
          <Form.Item
            name={[
              ...facilityPath(facilityName),
              A3ManufacturingKeys.PROCESS_ALLOCATION_SCOPE
            ]}
            label={A3ManufacturingLabels.PROCESS_ALLOCATION_SCOPE}
            rules={rulesWhenConfigured(
              [
                {
                  required: true,
                  message: CommonErrors.mandatory(
                    A3ManufacturingLabels.PROCESS_ALLOCATION_SCOPE
                  )
                }
              ],
              validateFields
            )}
          >
            <Radio.Group
              options={PROCESS_ALLOCATION_SCOPE_OPTIONS}
              onChange={onConfigure}
            />
          </Form.Item>
          <Form.List
            name={[...facilityPath(facilityName), A3ManufacturingKeys.PROCESS]}
          >
            {(fields, {add, remove}) => (
              <>
                <AddButton
                  onClick={() => {
                    onConfigure();
                    add(blankProcess());
                  }}
                >
                  Add Emission Record
                </AddButton>
                <div className="lci-cards-list">
                  {fields.map(({key, name: processName}, idx) => (
                    <RemovableCard
                      key={key}
                      title={`#${idx + 1}`}
                      onRemove={() => {
                        remove(processName);
                      }}
                      showRemove={fields.length > 1}
                    >
                      <ProcessFields
                        processName={processName}
                        validateFields={validateFields}
                      />
                    </RemovableCard>
                  ))}
                </div>
              </>
            )}
          </Form.List>
        </>
      )}
      {hasProcessEmissions === 'not_sure' && (
        <p className="field-hint">
          Examples include refrigerant top-ups, CO₂ from kilns or calcination,
          foam blowing agents, or SF₆ from electrical switchgear.
        </p>
      )}
    </div>
  );
}

function WaterTabPanel({
  facilityName,
  onConfigure
}: {
  facilityName: number;
  onConfigure: () => void;
}) {
  return (
    <div className="subsection-card">
      <Form.List
        name={[...facilityPath(facilityName), A3ManufacturingKeys.WATER]}
      >
        {(fields, {add, remove}) => (
          <>
            <div className="lci-section-header">
              <h4 className="subsection-title">Water Use</h4>
              <AddButton
                onClick={() => {
                  onConfigure();
                  add(blankWater());
                }}
              >
                Add Water Source
              </AddButton>
            </div>
            <div className="lci-cards-list">
              {fields.map(({key, name: waterName}, idx) => (
                <RemovableCard
                  key={key}
                  title={`#${idx + 1}`}
                  onRemove={() => {
                    remove(waterName);
                  }}
                  showRemove={fields.length > 1}
                >
                  <WaterFields waterName={waterName} />
                </RemovableCard>
              ))}
            </div>
          </>
        )}
      </Form.List>
    </div>
  );
}

function WasteTabPanel({
  facilityName,
  validateFields,
  onConfigure
}: {
  facilityName: number;
  validateFields: boolean;
  onConfigure: () => void;
}) {
  const form = Form.useFormInstance();
  const wasteTypesSelected =
    Form.useWatch(
      [...facilityPath(facilityName), A3ManufacturingKeys.WASTE_TYPES_SELECTED],
      form
    ) ?? [];

  return (
    <div className="subsection-card">
      <div className="lci-section-header">
        <h4 className="subsection-title">
          Waste &amp; Emissions to Environment
        </h4>
      </div>
      <Form.Item
        name={[
          ...facilityPath(facilityName),
          A3ManufacturingKeys.WASTE_TYPES_SELECTED
        ]}
        label={A3ManufacturingLabels.WASTE_TYPES_SELECTED}
      >
        <Checkbox.Group
          options={WASTE_CHECKLIST_OPTIONS}
          onChange={onConfigure}
        />
      </Form.Item>
      {!wasteTypesSelected?.length && (
        <Form.Item
          name={[
            ...facilityPath(facilityName),
            A3ManufacturingKeys.NO_WASTE_JUSTIFICATION
          ]}
          required={validateFields}
          label={
            <FormItemLabel hint={LCI_FIELD_HINTS.noWasteJustification}>
              {A3ManufacturingLabels.NO_WASTE_JUSTIFICATION}
            </FormItemLabel>
          }
          rules={
            validateFields
              ? noWasteJustificationRule(() => wasteTypesSelected ?? [])
              : []
          }
        >
          <Input.TextArea rows={2} className="input" maxLength={300} />
        </Form.Item>
      )}
      <Form.List
        name={[...facilityPath(facilityName), A3ManufacturingKeys.WASTE]}
      >
        {(fields, {add, remove}) => (
          <>
            <div className="lci-section-header" style={{marginTop: 0}}>
              <AddButton
                onClick={() => {
                  onConfigure();
                  add(blankWaste());
                }}
              >
                Add Waste Stream
              </AddButton>
            </div>
            <div className="lci-cards-list">
              {fields.map(({key, name: wasteName}, idx) => (
                <RemovableCard
                  key={key}
                  title={`#${idx + 1}`}
                  onRemove={() => {
                    remove(wasteName);
                  }}
                  showRemove={fields.length > 1}
                >
                  <WasteFields
                    wasteName={wasteName}
                    validateFields={validateFields}
                  />
                </RemovableCard>
              ))}
            </div>
          </>
        )}
      </Form.List>
    </div>
  );
}

function FacilityTabbedContent({
  facilityName,
  expertMode,
  siteState,
  validateFields,
  isConfigured,
  showMultiSite,
  onConfigure
}: {
  facilityName: number;
  expertMode: boolean;
  siteState: string;
  validateFields: boolean;
  isConfigured: boolean;
  showMultiSite: boolean;
  onConfigure: () => void;
}) {
  const [activeCategoryTab, setActiveCategoryTab] =
    useState<A3CategoryTabId>('energy');

  const handleConfigureInteraction = () => {
    onConfigure();
  };

  return (
    <div className="a3-facility-subsections">
      {showMultiSite && !isConfigured && (
        <p className="a3-unconfigured-hint field-hint">
          Enter data below to configure this site for A3.
        </p>
      )}

      <A3CategoryTabs active={activeCategoryTab} onChange={setActiveCategoryTab} />

      <div
        className="a3-facility-tab-content"
        onInput={handleConfigureInteraction}
        onClick={(event) => {
          const target = event.target as HTMLElement;

          if (
            target.closest(
              'input, textarea, button, .ant-select, .ant-radio, .ant-checkbox, .ant-switch'
            )
          ) {
            handleConfigureInteraction();
          }
        }}
      >
        {activeCategoryTab === 'energy' && (
          <EnergyTabPanel
            facilityName={facilityName}
            expertMode={expertMode}
            siteState={siteState}
            validateFields={validateFields}
            onConfigure={onConfigure}
          />
        )}
        {activeCategoryTab === 'process' && (
          <ProcessTabPanel
            facilityName={facilityName}
            validateFields={validateFields}
            onConfigure={onConfigure}
          />
        )}
        {activeCategoryTab === 'water' && (
          <WaterTabPanel
            facilityName={facilityName}
            onConfigure={onConfigure}
          />
        )}
        {activeCategoryTab === 'waste' && (
          <WasteTabPanel
            facilityName={facilityName}
            validateFields={validateFields}
            onConfigure={onConfigure}
          />
        )}
      </div>
    </div>
  );
}

function A3FacilityRemoveControl({
  disabled,
  onRemove
}: {
  disabled: boolean;
  onRemove: () => void;
}) {
  return (
    <span className="a3-facility-remove-tooltip-wrap">
      <Popconfirm
        title="Clear this site's data?"
        okText="Clear"
        cancelText="Cancel"
        disabled={disabled}
        overlayClassName="a3-facility-remove-popconfirm"
        onConfirm={onRemove}
      >
        <Button
          htmlType="button"
          shape="circle"
          type="link"
          className="a3-facility-remove-btn"
          disabled={disabled}
          icon={<Delete aria-hidden />}
          aria-label="Clear facility data"
        />
      </Popconfirm>
    </span>
  );
}

function A3FacilitySelect({
  facilities,
  productionSites,
  activeIndex,
  onChange,
  onRemove
}: {
  facilities: FacilityRow[];
  productionSites: ProductionSiteRow[];
  activeIndex: number;
  onChange: (index: number) => void;
  onRemove: () => void;
}) {
  const activeFacility = facilities[activeIndex];
  const isActiveConfigured = Boolean(activeFacility?.configured);

  const options = facilities.map((facility, index) => {
    const displayName = getFacilityDisplayName(
      facility,
      productionSites[index],
      index
    );
    const stateLabel = getStateLabel(facility.state || 'tamil_nadu');

    return {
      value: index,
      label: `${displayName} (${stateLabel})`,
      configured: Boolean(facility.configured)
    };
  });

  const renderOptionLabel = (
    label: string,
    configured: boolean
  ): React.ReactNode => (
    <span className="a3-facility-option">
      {configured && (
        <CheckOutlined
          aria-hidden
          className="a3-facility-option-tick"
        />
      )}
      <span>{label}</span>
    </span>
  );

  return (
    <div className="a3-facility-select-wrap">
      <Select
        className="select a3-facility-select"
        value={activeIndex}
        options={options}
        optionRender={(option) =>
          renderOptionLabel(
            String(option.label ?? ''),
            Boolean(option.data.configured)
          )
        }
        {...({
          labelRender: () => {
            const selected = options[activeIndex];

            return renderOptionLabel(
              String(selected?.label ?? ''),
              Boolean(selected?.configured)
            );
          }
        } as any)}
        onChange={(value) => {
          onChange(Number(value));
        }}
      />
      <A3FacilityRemoveControl
        disabled={!isActiveConfigured}
        onRemove={onRemove}
      />
    </div>
  );
}

const A3ManufacturingSection: React.FC = () => {
  const form = Form.useFormInstance();
  const expertMode = useExpertMode();
  const productionFallback = usePCFStore(
    (state) => state.recordData.lci?.production
  );
  Form.useWatch('production', form);
  Form.useWatch(BASE, form);
  const production: ProductionDataValues =
    getProductionFromForm(form, productionFallback) ?? productionDataDefaults;
  const a3 =
    (form.getFieldsValue(true) as LciFormValues).a3 ?? a3ManufacturingDefaults;
  const facilities = a3.facilities ?? [];
  const activeFacility = a3.activeFacility ?? null;
  const resolvedActiveFacility = resolveActiveFacilityIndex(
    facilities,
    activeFacility
  );
  const [activeFacilityIndex, setActiveFacilityIndex] = useState(
    resolvedActiveFacility ?? 0
  );

  const productionSyncKey = useMemo(
    () =>
      [
        production.setup,
        production.sites?.length ?? 0,
        production.allocationBasis,
        ...(production.sites ?? []).map(
          (site) =>
            `${site.name ?? ''}|${site.state ?? ''}|${site.facilityThroughput ?? ''}`
        )
      ].join('::'),
    [
      production.setup,
      production.allocationBasis,
      production.sites?.length,
      production.sites
        ?.map(
          (site) =>
            `${site.name ?? ''}|${site.state ?? ''}|${site.facilityThroughput ?? ''}`
        )
        .join('::')
    ]
  );

  useLayoutEffect(() => {
    syncA3FacilitiesFromProduction(form, productionFallback);
  }, [form, productionFallback, productionSyncKey]);

  const watchedActiveFacility = Form.useWatch([BASE, 'activeFacility'], form);

  useEffect(() => {
    const resolved = resolveActiveFacilityIndex(
      facilities,
      watchedActiveFacility ?? activeFacility
    );

    if (resolved == null) {
      return;
    }

    setActiveFacilityIndex((current) =>
      current === resolved ? current : resolved
    );
  }, [watchedActiveFacility, activeFacility, facilities.length]);

  useEffect(() => {
    if (facilities.length > 0 && activeFacilityIndex >= facilities.length) {
      const nextIndex = facilities.length - 1;
      setActiveFacilityIndex(nextIndex);
      form.setFieldValue([BASE, 'activeFacility'], nextIndex);
    }
  }, [facilities.length, form, activeFacilityIndex]);

  const setup = production.setup ?? 'single_site';
  const showMultiSite = isMultiSiteSetup(setup);

  const handleFacilityChange = (index: number) => {
    setActiveFacilityIndex(index);
    form.setFieldValue([BASE, 'activeFacility'], index);
  };

  const markActiveFacilityConfigured = useCallback(() => {
    if (!showMultiSite) {
      return;
    }

    const configuredPath = [
      BASE,
      A3ManufacturingKeys.FACILITIES,
      activeFacilityIndex,
      A3ManufacturingKeys.CONFIGURED
    ];

    if (!form.getFieldValue(configuredPath)) {
      form.setFieldValue(configuredPath, true);
    }
  }, [activeFacilityIndex, form, showMultiSite]);

  const handleRemoveActiveFacility = () => {
    resetA3FacilityAtIndex(form, activeFacilityIndex, production);
  };

  const activeFacilityRow = facilities[activeFacilityIndex];
  const isActiveConfigured = showMultiSite
    ? Boolean(activeFacilityRow?.configured)
    : true;
  const validateFields = isActiveConfigured;

  if (!facilities.length) {
    return (
      <div className="lci-section">
        <h3 className="lci-section-title">
          A3 · Manufacturing &amp; Processing
        </h3>
        <div className="empty-facilities">
          <p className="empty-facilities-text">
            {showMultiSite
              ? 'Add at least one manufacturing site under Production Data — A3 ' +
                'facilities are created from those site rows.'
              : 'Complete Production Data first — manufacturing sites are created from your production setup.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lci-section">
      <div className="a3-section-header">
        <h3 className="lci-section-title">
          A3 · Manufacturing &amp; Processing
        </h3>
        {showMultiSite && (
          <A3FacilitySelect
            facilities={facilities}
            productionSites={production.sites ?? []}
            activeIndex={activeFacilityIndex}
            onChange={handleFacilityChange}
            onRemove={handleRemoveActiveFacility}
          />
        )}
      </div>

      <Form.Item name={[BASE, 'activeFacility']} hidden>
        <span />
      </Form.Item>
      <Form.Item
        name={[
          BASE,
          A3ManufacturingKeys.FACILITIES,
          activeFacilityIndex,
          A3ManufacturingKeys.CONFIGURED
        ]}
        hidden
      >
        <span />
      </Form.Item>

      <FacilityTabbedContent
        key={activeFacilityIndex}
        facilityName={activeFacilityIndex}
        expertMode={expertMode}
        siteState={activeFacilityRow?.state || 'tamil_nadu'}
        validateFields={validateFields}
        isConfigured={isActiveConfigured}
        showMultiSite={showMultiSite}
        onConfigure={markActiveFacilityConfigured}
      />
    </div>
  );
};

export default A3ManufacturingSection;
